import type { Leaderboard, Tally } from "@hali-shares/deck-schema";
import type { CSSProperties } from "react";
import { usePresenter } from "./context";
import { JoinCard } from "./JoinCard";
import type { PollBlock, QuizBlock } from "./model";

type Row = { id: string; key: string; label: string };

export function pollRows(block: PollBlock | QuizBlock): Row[] {
  if (block.type === "poll" && block.kind === "scale") {
    const { min = 1, max = 5, minLabel, maxLabel } = block.scale ?? {};
    return Array.from({ length: scaleSteps(min, max) }, (_, i) => {
      const v = min + i;
      return { id: String(v), key: String(v), label: v === min ? (minLabel ?? "") : v === max ? (maxLabel ?? "") : "" };
    });
  }
  return block.options.map((o, i) => ({ id: o.id, key: String.fromCharCode(65 + i), label: o.label }));
}

export function Check() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-label="Correct"
    >
      <path d="M3.5 9.5 L7.5 13 L14.5 5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * Poll and quiz slides. The vote count is live; percentages, bars and answers stay hidden until reveal.
 * Counts come from the server's poll:tally (presenter only), never from anything a phone sent.
 */
export function PollSlide({ block }: { block: PollBlock | QuizBlock }) {
  const { poll, togglePoll, tally, leaderboard } = usePresenter();
  const { locked, revealed } = poll(block.id);
  const t = tally(block.id);
  const total = t?.total ?? 0;
  const free = block.type === "poll" && (block.kind === "wordcloud" || block.kind === "open") ? block.kind : null;
  const noun = free ? (total === 1 ? "answer" : "answers") : total === 1 ? "vote" : "votes";

  return (
    <div className="slide poll-slide">
      <div className="poll-main">
        <div className="poll-head">
          <span className="eyebrow">
            {block.type === "quiz" ? "Quiz" : "Live poll"} · {locked ? "Locked" : "Open"}
          </span>
          <h2 className="poll-q">{block.question}</h2>
        </div>
        {free ? (
          <FreeAnswers kind={free} revealed={revealed} tally={t} />
        ) : block.type === "poll" && block.kind === "scale" ? (
          <ScaleBars block={block} revealed={revealed} tally={t} />
        ) : (
          <ChoiceBars block={block} revealed={revealed} tally={t} />
        )}
        {revealed && block.type === "quiz" && block.explanation && (
          <p className="explanation" style={{ margin: 0 }}>
            {block.explanation}
          </p>
        )}
        <div className="poll-foot">
          <span className="poll-total" data-testid="vote-total">
            <b>{total}</b> {noun}
          </span>
          <button
            type="button"
            className="btn btn-lg"
            onClick={() => togglePoll(block.id, "locked")}
            aria-keyshortcuts="L"
          >
            {locked ? "Unlock voting" : "Lock voting"}
          </button>
          <button
            type="button"
            className="btn btn-lg btn-ink"
            onClick={() => togglePoll(block.id, "revealed")}
            aria-keyshortcuts="R"
          >
            {revealed ? "Hide results" : "Reveal results"}
          </button>
        </div>
      </div>
      {block.type === "quiz" && revealed && leaderboard && leaderboard.entries.length > 0 ? (
        <LeaderboardCard board={leaderboard} />
      ) : (
        <JoinCard verb="Vote" size="md" />
      )}
    </div>
  );
}

/** Shown in place of the join card once a quiz is revealed, if anyone has a nickname. */
function LeaderboardCard({ board }: { board: Leaderboard }) {
  const top = board.entries[0]?.score ?? 0;
  return (
    <div className="leaderboard" data-testid="leaderboard">
      <span className="eyebrow" style={{ marginBottom: 6 }}>
        Leaderboard · {board.quizzes} {board.quizzes === 1 ? "quiz" : "quizzes"}
      </span>
      {board.entries.slice(0, 8).map((e, i) => {
        // Ties share a place.
        const place = board.entries.findIndex((x) => x.score === e.score) + 1;
        return (
          <div key={`${e.name}-${i}`} className="lb-row" data-first={e.score === top && top > 0}>
            <span className="lb-rank">{place}</span>
            <span className="lb-name">{e.name}</span>
            <span className="lb-score">{e.score}</span>
          </div>
        );
      })}
    </div>
  );
}

function ChoiceBars({ block, revealed, tally }: { block: PollBlock | QuizBlock; revealed: boolean; tally?: Tally }) {
  const rows = pollRows(block);
  const counts = tally?.counts ?? {};
  const total = tally?.total ?? 0;
  const max = Math.max(0, ...rows.map((o) => counts[o.id] ?? 0));
  const correct = block.type === "quiz" ? block.correctOptionId : undefined;
  return (
    <div className="options">
      {rows.map((o) => {
        const n = counts[o.id] ?? 0;
        // Multi-select: shares of voters, so they can add up to more than 100%.
        const pct = total ? Math.round((n / total) * 100) : 0;
        const lead = correct ? o.id === correct : n === max && max > 0;
        return (
          <div key={o.id} className="option" data-option={o.id}>
            <div className="option-row">
              <span className="option-label">
                <span className="option-key">{o.key}</span>
                <span>{o.label}</span>
              </span>
              <span className="option-pct">
                {revealed && correct === o.id && <Check />}
                {revealed ? `${pct}%` : "–"}
              </span>
            </div>
            <div className="bar">
              <div
                className="bar-fill"
                data-lead={revealed && lead}
                // Share of everyone who voted, like the label: two single votes are two half bars, not two full ones.
                style={{ width: revealed && total ? `${pct}%` : 0 }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** A rating scale reads left to right: one column per point, end labels under their numbers, the average once revealed. */
function ScaleBars({ block, revealed, tally }: { block: PollBlock; revealed: boolean; tally?: Tally }) {
  const rows = pollRows(block);
  const counts = tally?.counts ?? {};
  const total = tally?.total ?? 0;
  const max = Math.max(0, ...rows.map((o) => counts[o.id] ?? 0));
  const sum = rows.reduce((n, o) => n + Number(o.key) * (counts[o.id] ?? 0), 0);
  return (
    <div className="scale" data-testid="scale">
      <div className="scale-cols">
        {rows.map((o) => {
          const n = counts[o.id] ?? 0;
          return (
            <div key={o.id} className="scale-col" data-option={o.id}>
              <span className="option-pct">{revealed ? `${total ? Math.round((n / total) * 100) : 0}%` : "–"}</span>
              <div className="scale-track">
                <div
                  className="bar-fill"
                  data-lead={revealed && n === max && max > 0}
                  style={{ height: revealed && total ? `${Math.round((n / total) * 100)}%` : 0 }}
                />
              </div>
              <span className="scale-key">{o.key}</span>
              <span className="scale-end">{o.label}</span>
            </div>
          );
        })}
      </div>
      {/* Always there, so revealing doesn't push the buttons down. */}
      <span className="scale-avg">{revealed && total > 0 ? `Average ${(sum / total).toFixed(1)}` : ""}</span>
    </div>
  );
}

/**
 * Word clouds and open answers. Before reveal: how many are in. After: a cloud with the most-said words largest and
 * in the middle, or the top answers as cards. Both appear one by one (instantly with reduced motion).
 */
function FreeAnswers({ kind, revealed, tally }: { kind: "wordcloud" | "open"; revealed: boolean; tally?: Tally }) {
  const words = tally?.words ?? [];
  const noun = kind === "wordcloud" ? "word" : "answer";
  if (!revealed || words.length === 0)
    return (
      <div className="answers-empty" data-kind={kind}>
        {words.length === 0 ? (
          <span>
            {kind === "wordcloud" ? "Words appear here once people send them." : "Answers appear here once people send them."}
          </span>
        ) : (
          <>
            <b className="answers-empty-n">{words.length}</b>
            <span>
              different {noun}
              {words.length === 1 ? "" : "s"} in. Reveal to show them.
            </span>
          </>
        )}
      </div>
    );
  if (kind === "open")
    return (
      <ol className="answers" data-testid="answers">
        {words.slice(0, 6).map((w, i) => (
          <li key={w.key} className="answer" data-top={i === 0} style={{ "--i": i } as CSSProperties}>
            <span className="answer-text">{w.text}</span>
            <span className="answer-up">
              {w.n > 1 && <span className="answer-n">×{w.n}</span>}
              {(w.up ?? 0) > 0 && (
                <>
                  <UpvoteIcon />
                  {w.up}
                </>
              )}
            </span>
          </li>
        ))}
      </ol>
    );
  const shown = words.slice(0, 24);
  const top = shown[0]!.n;
  const low = shown[shown.length - 1]!.n;
  return (
    <div className="cloud" data-testid="cloud">
      {centreOut(shown).map(({ w, rank }) => (
        // --w runs 0..1 between the least and most said, and picks a size between the cloud tokens.
        <span
          key={w.key}
          className="cloud-word"
          data-top={rank === 0 && w.n > 1 && w.n > (shown[1]?.n ?? 0)}
          style={{ "--w": top > low ? (w.n - low) / (top - low) : 0.5, "--i": rank } as CSSProperties}
        >
          {w.text}
          {w.n > 1 && <sup className="cloud-n">{w.n}</sup>}
        </span>
      ))}
    </div>
  );
}

/** Most said first, then alternately either side of it: the biggest words sit in the middle of the cloud. */
function centreOut<T>(sorted: T[]): { w: T; rank: number }[] {
  const out: { w: T; rank: number }[] = [];
  sorted.forEach((w, rank) => (rank % 2 ? out.unshift({ w, rank }) : out.push({ w, rank })));
  return out;
}

export function UpvoteIcon() {
  return (
    <svg
      aria-label="upvotes"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 10V2.5M2.5 6 6 2.5 9.5 6" />
    </svg>
  );
}

/** Points on a scale. Clamped: a half-edited scale (from > to, or 1..10000) mustn't break rendering. */
function scaleSteps(min: number, max: number): number {
  return Math.min(Math.max(0, max - min + 1), 21);
}
